import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import AdminLayout from "../../components/admin/AdminLayout";
import { ArrowLeft, PencilSimple, QrCode, Check, X } from "@phosphor-icons/react";

export default function AdminOutletDetail() {
  const { outletId } = useParams();
  const [outlet, setOutlet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchOutlet(); }, [outletId]);

  async function fetchOutlet() {
    setLoading(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/outlets/${outletId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("rs_token")}` },
      });
      if (!res.ok) throw new Error("Outlet not found");
      const data = await res.json();
      setOutlet(data);
      setEditForm({
        name: data.name,
        google_review_url: data.google_review_url || "",
        require_screenshot: data.require_screenshot || false,
        allow_multiple_spins: data.allow_multiple_spins || false,
      });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function saveChanges() {
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/outlets/${outletId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("rs_token")}`,
        },
        body: JSON.stringify(editForm),
      });
      if (!res.ok) throw new Error("Failed to update");
      toast.success("Outlet updated");
      setEditing(false);
      fetchOutlet();
    } catch (e) {
      toast.error(e.message);
    }
  }

  if (loading) return <AdminLayout><div className="flex items-center justify-center h-64"><div className="tag">Loading...</div></div></AdminLayout>;
  if (!outlet) return <AdminLayout><div className="text-center py-20 text-[var(--ink-muted)]">Outlet not found</div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <Link to="/admin/outlets" className="p-2 rounded-lg hover:bg-gray-100"><ArrowLeft size={20} /></Link>
          <div>
            <h2 className="font-black text-xl">{outlet.name}</h2>
            <p className="text-sm text-[var(--ink-muted)]">Owner: {outlet.users?.name || "Unknown"} · Slug: {outlet.slug}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {!editing ? (
              <button onClick={() => setEditing(true)} className="btn-pop text-sm flex items-center gap-1.5">
                <PencilSimple size={14} /> Edit Config
              </button>
            ) : (
              <>
                <button onClick={() => setEditing(false)} className="btn-pop text-sm bg-gray-100"><X size={14} /> Cancel</button>
                <button onClick={saveChanges} className="btn-primary text-sm flex items-center gap-1.5"><Check size={14} /> Save</button>
              </>
            )}
          </div>
        </div>

        <div className="grid lg:grid-cols-4 gap-4">
          <div className="card-pop p-4"><div className="text-xs font-bold text-[var(--ink-muted)] uppercase tracking-wider">Scans</div><div className="text-2xl font-black mt-1">{outlet.scans_count}</div></div>
          <div className="card-pop p-4"><div className="text-xs font-bold text-[var(--ink-muted)] uppercase tracking-wider">Spins</div><div className="text-2xl font-black mt-1">{outlet.spins_count}</div></div>
          <div className="card-pop p-4"><div className="text-xs font-bold text-[var(--ink-muted)] uppercase tracking-wider">Wins</div><div className="text-2xl font-black mt-1">{outlet.wins_count}</div></div>
          <div className="card-pop p-4"><div className="text-xs font-bold text-[var(--ink-muted)] uppercase tracking-wider">Redemptions</div><div className="text-2xl font-black mt-1">{outlet.redemptions_count}</div></div>
        </div>

        {editing ? (
          <div className="card-pop p-5 space-y-4">
            <h3 className="font-bold">Edit Configuration</h3>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Name</label>
              <input className="input-pop mt-1" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})} />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-widest text-[var(--ink-muted)]">Google Review URL</label>
              <input className="input-pop mt-1" value={editForm.google_review_url} onChange={e => setEditForm({...editForm, google_review_url: e.target.value})} />
            </div>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editForm.require_screenshot} onChange={e => setEditForm({...editForm, require_screenshot: e.target.checked})} />
                <span className="text-sm font-medium">Require Screenshot</span>
              </label>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={editForm.allow_multiple_spins} onChange={e => setEditForm({...editForm, allow_multiple_spins: e.target.checked})} />
                <span className="text-sm font-medium">Allow Multiple Spins</span>
              </label>
            </div>
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 gap-5">
            <div className="card-pop p-4 space-y-3">
              <h3 className="font-bold">Configuration</h3>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="text-[var(--ink-muted)]">Review URL</div><div className="font-medium break-all">{outlet.google_review_url || "-"}</div>
                <div className="text-[var(--ink-muted)]">Screenshot Required</div><div className="font-medium">{outlet.require_screenshot ? "Yes" : "No"}</div>
                <div className="text-[var(--ink-muted)]">Multiple Spins</div><div className="font-medium">{outlet.allow_multiple_spins ? "Yes" : "No"}</div>
              </div>
            </div>
            <div className="card-pop p-4 space-y-3">
              <h3 className="font-bold">Spin Wheel Segments</h3>
              <div className="space-y-2">
                {(outlet.segments || []).map((s, i) => (
                  <div key={i} className="flex items-center gap-3 p-2 rounded-lg bg-gray-50">
                    <div className="h-4 w-4 rounded-full flex-shrink-0" style={{ background: s.color }} />
                    <div className="flex-1">
                      <div className="text-sm font-bold">{s.label}</div>
                      <div className="text-xs text-[var(--ink-muted)]">{s.type} · Weight: {s.weight}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="card-pop p-4">
          <h3 className="font-bold mb-3">Recent Spin Attempts</h3>
          {(outlet.recent_attempts || []).length === 0 ? (
            <p className="text-sm text-[var(--ink-muted)] text-center py-4">No attempts yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2" style={{ borderColor: "var(--border)" }}>
                    <th className="py-2 px-3 text-left font-bold">Time</th>
                    <th className="py-2 px-3 text-left font-bold">Reward</th>
                    <th className="py-2 px-3 text-left font-bold">Code</th>
                    <th className="py-2 px-3 text-left font-bold">Redeemed</th>
                  </tr>
                </thead>
                <tbody>
                  {outlet.recent_attempts.map((a) => (
                    <tr key={a.id} className="border-b" style={{ borderColor: "var(--border)" }}>
                      <td className="py-2 px-3">{new Date(a.created_at).toLocaleString()}</td>
                      <td className="py-2 px-3 font-medium">{a.reward_label}</td>
                      <td className="py-2 px-3 font-mono text-xs">{a.redemption_code || "-"}</td>
                      <td className="py-2 px-3">{a.redeemed ? "Yes" : "No"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}